import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, X, ArrowRight, MessageSquare, Maximize2 } from 'lucide-react';

export default function CoachBubble() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const inputRef = useRef(null);
  const isCoachPage = location.pathname === '/nutrition-coach';

  // Focus input when popover opens
  useEffect(() => {
    if (isCoachPage || !open) return;
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, isCoachPage]);

  // Close on Escape
  useEffect(() => {
    if (isCoachPage) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isCoachPage]);

  // Don't show on the coach page itself
  if (isCoachPage) return null;

  const handleOpen = () => {
    setOpen(o => !o);
    setQuestion('');
  };

  const openFullCoach = () => {
    setOpen(false);
    navigate('/nutrition-coach');
  };

  const askQuestion = (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setOpen(false);
    navigate(`/nutrition-coach?q=${encodeURIComponent(question.trim())}`);
    setQuestion('');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

      {/* Popover card */}
      {open && (
        <div className="w-72 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150">

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-semibold">AI Nutrition Coach</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">

            {/* Quick ask */}
            <form onSubmit={askQuestion}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Quick Ask</p>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  placeholder="Ask anything..."
                  className="flex-1 text-sm px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white placeholder-gray-400"
                />
                <button
                  type="submit"
                  disabled={!question.trim()}
                  className="w-9 h-9 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex-shrink-0"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>

            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Full Experience</p>
              <button
                onClick={openFullCoach}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/30 hover:from-indigo-100 hover:to-violet-100 dark:hover:from-indigo-900/50 dark:hover:to-violet-900/50 border border-indigo-100 dark:border-indigo-800 rounded-xl transition-colors text-sm font-medium text-indigo-700 dark:text-indigo-300"
              >
                <span className="flex items-center gap-2">
                  <Maximize2 className="w-3.5 h-3.5" />
                  Open Nutrition Coach
                </span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={handleOpen}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 ${
          open
            ? 'bg-gray-700 dark:bg-gray-600 rotate-0 scale-95'
            : 'bg-gradient-to-br from-indigo-600 to-violet-600 hover:scale-110 hover:shadow-indigo-500/40'
        }`}
        aria-label="Open Nutrition Coach"
      >
        {open
          ? <X className="w-5 h-5 text-white" />
          : <Brain className="w-6 h-6 text-white" />
        }
        {/* Pulse ring when closed */}
        {!open && (
          <span className="absolute w-14 h-14 rounded-full bg-indigo-400/30 animate-ping" />
        )}
      </button>

    </div>
  );
}
